// Imperative runtime helpers ported from public/assets/player.js. They operate
// on a passed-in stage element (the React-owned <div className="stage">), so the
// game engines stay imperative islands while React owns the surrounding chrome.

// Era applets grabbed keyboard focus on load; browsers don't. Without this a
// player's first Space/arrow press scrolls the page and never reaches the game.
const GAME_KEYS = new Set([
  " ",
  "Spacebar",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Home",
  "End",
]);

export function runtimeElement(stage: HTMLElement): HTMLElement | null {
  return stage.querySelector<HTMLElement>(
    "ruffle-player, ruffle-embed, ruffle-object, iframe, embed, object, canvas, cheerpj-applet",
  );
}

export function focusRuntime(stage: HTMLElement): boolean {
  const el = runtimeElement(stage);
  if (!el) return false;
  if (!el.hasAttribute("tabindex") && !["IFRAME", "EMBED", "OBJECT"].includes(el.tagName)) {
    el.setAttribute("tabindex", "0");
  }
  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
  return document.activeElement === el || el.contains(document.activeElement);
}

// Keep offering focus to the (async-mounting) runtime, but never steal it from
// something the user focused. Blocks page scroll only while the game has focus.
// Returns a cleanup that removes the interval + listeners.
export function keepGameFocused(stage: HTMLElement): () => void {
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    const active = document.activeElement;
    const userIsElsewhere = active && active !== document.body && !stage.contains(active);
    if ((!userIsElsewhere && focusRuntime(stage)) || tries > 120) window.clearInterval(timer);
  }, 500);

  const onPointer = () => setTimeout(() => focusRuntime(stage), 0);
  stage.addEventListener("pointerdown", onPointer);

  const onKey = (event: KeyboardEvent) => {
    if (!GAME_KEYS.has(event.key)) return;
    const el = runtimeElement(stage);
    if (!el) return;
    const active = document.activeElement;
    if (el === active || el.contains(active)) event.preventDefault();
  };
  window.addEventListener("keydown", onKey, { capture: true });

  return () => {
    window.clearInterval(timer);
    stage.removeEventListener("pointerdown", onPointer);
    window.removeEventListener("keydown", onKey, { capture: true });
  };
}

export function showStageStatus(stage: HTMLElement, text: string): HTMLDivElement {
  const status = document.createElement("div");
  status.className = "stage-status";
  status.textContent = text;
  stage.append(status);
  return status;
}

export function emptyNotice(stage: HTMLElement, text: string): void {
  const p = document.createElement("p");
  p.className = "empty";
  p.textContent = text;
  stage.replaceChildren(p);
}

// Load a global, side-effecting <script> (Ruffle, CheerpJ). Deduped by id and
// retried, so navigating between games never reloads the heavy runtime script.
export function loadExternalScript(id: string, src: string, options: { retries?: number } = {}): Promise<void> {
  const retries = options.retries ?? 1;
  const existing = document.getElementById(id) as HTMLScriptElement | null;
  if (existing?.dataset.loadState === "loaded") return Promise.resolve();
  if (existing?.dataset.loadState === "loading") {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`Could not load ${src}`)), { once: true });
    });
  }
  existing?.remove();
  return loadScriptAttempt(id, src, retries);
}

function loadScriptAttempt(id: string, src: string, retriesRemaining: number): Promise<void> {
  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;
  script.dataset.loadState = "loading";
  return new Promise((resolve, reject) => {
    script.addEventListener(
      "load",
      () => {
        script.dataset.loadState = "loaded";
        resolve();
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      async () => {
        script.dataset.loadState = "error";
        script.remove();
        if (retriesRemaining > 0) {
          await new Promise((r) => setTimeout(r, 600));
          try {
            await loadScriptAttempt(id, src, retriesRemaining - 1);
            resolve();
          } catch (err) {
            reject(err);
          }
          return;
        }
        reject(new Error(`Could not load ${src}`));
      },
      { once: true },
    );
    document.head.append(script);
  });
}

export async function waitForRuntime(checkReady: () => unknown, timeoutMs = 12000): Promise<boolean> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (checkReady()) return true;
    await new Promise((r) => setTimeout(r, 100));
  }
  return false;
}
